import {
  IFirebaseService,
  ISviDashboardService,
} from "../interfaces/services.interfaces";
import AbstractService from "./abstract.service";
import { env } from "../env";
import {
  resolveProgressVariantForQuarter,
} from "./svidashboard-progress.manifest";

export default class SviDashboardService
  extends AbstractService
  implements ISviDashboardService
{
  constructor({ firebaseService }: { firebaseService: IFirebaseService }) {
    super({ firebaseService });
  }

  public async getDashboardImage(
    objectId: string,
    year: number,
    quarter: number,
    toggle: boolean,
  ): Promise<{
    data: {
      success: boolean;
      url?: string;
      date?: string | null;
      key?: string;
      message?: string;
    };
  }> {
    const url = `${env.REACT_APP_SERVER_URL}/svidashboard/image?objectId=${encodeURIComponent(
      objectId,
    )}&year=${year}&quarter=${quarter}&toggle=${toggle}`;
    return this.axiosInstance.get(url);
  }

  public async getProgressVariantImage(
    objectId: string,
    year: number,
    quarter: number,
    variant: 'lightGreen' | 'darkGreen',
  ): Promise<{
    data: {
      success: boolean;
      url?: string;
      date?: string | null;
      key?: string;
      message?: string;
    };
  }> {
    const resolvedVariant = resolveProgressVariantForQuarter(
      objectId,
      year,
      quarter,
      variant,
    );

    if (resolvedVariant === null) {
      return {
        data: {
          success: false,
          message: 'File Not Found',
        },
      };
    }

    const url = `${env.REACT_APP_SERVER_URL}/svidashboard/progress-image?objectId=${encodeURIComponent(
      objectId,
    )}&year=${year}&quarter=${quarter}&variant=${encodeURIComponent(
      resolvedVariant,
    )}`;

    try {
      const response = await this.axiosInstance.get(url);
      if (response.data?.success && response.data.url) {
        return response;
      }
    } catch {
      // Fall back to direct S3 key resolution for objects 4/5.
    }

    const { resolveProgressVariantImage } = await import(
      './svidashboard-progress.client'
    );
    const resolved = await resolveProgressVariantImage(
      objectId,
      year,
      quarter,
      resolvedVariant,
    );

    return { data: resolved };
  }

  public async getQuarterProgress(
    year: number,
    quarter: number,
  ): Promise<{
    data: {
      success: boolean;
      data: {
        year: number;
        quarter: number;
        currentProgressPercent: number;
        expertProgressPercent: number;
        comments: Array<{ text: string; date: string }>;
      } | null;
    };
  }> {
    const url = `${env.REACT_APP_SERVER_URL}/svidashboard/progress?year=${year}&quarter=${quarter}`;
    return this.axiosInstance.get(url);
  }

  public async addExpertEstimation(
    year: number,
    quarter: number,
    value: number,
  ): Promise<{
    data: {
      success: boolean;
      data: {
        year: number;
        quarter: number;
        currentProgressPercent: number;
        expertProgressPercent: number;
        comments: Array<{ text: string; date: string }>;
      };
    };
  }> {
    const url = `${env.REACT_APP_SERVER_URL}/svidashboard/progress/estimate`;
    return this.axiosInstance.post(url, { year, quarter, value });
  }

  public async addQuarterComment(
    year: number,
    quarter: number,
    text: string,
  ): Promise<{
    data: {
      success: boolean;
      data: {
        year: number;
        quarter: number;
        currentProgressPercent: number;
        expertProgressPercent: number;
        comments: Array<{ text: string; date: string }>;
      };
    };
  }> {
    const url = `${env.REACT_APP_SERVER_URL}/svidashboard/progress/comment`;
    return this.axiosInstance.post(url, { year, quarter, text });
  }

  public async getAllComments(): Promise<{
    data: {
      success: boolean;
      data: Array<{
        text: string;
        date: string;
        year?: number;
        quarter?: number;
      }>;
    };
  }> {
    const url = `${env.REACT_APP_SERVER_URL}/svidashboard/comments`;
    return this.axiosInstance.get(url);
  }

  public async getTimelapseImages(objectId: string): Promise<{
    data: {
      success: boolean;
      frames?: Array<{
        year: number;
        quarter: number;
        title: string;
        date: string | null;
        url: string;
        key: string;
      }>;
      message?: string;
    };
  }> {
    const url = `${env.REACT_APP_SERVER_URL}/svidashboard/timelapse?objectId=${encodeURIComponent(
      objectId,
    )}`;
    return this.axiosInstance.get(url);
  }
}

